/**
 * Client source of truth, persisted to `localStorage`.
 *
 * Pure-functional: `applyGrade` and `completeLesson` never mutate their
 * input `AppState` — they return a new object. `now` (epoch ms) and `rand`
 * (a `[0, 1)` source) are always injected by the caller, mirroring the
 * `@repo/hindi-shared` SRS/session modules — never `Date.now()` or
 * `Math.random()` in here.
 */
import type { Grade, ItemKind, ReviewState } from "@repo/hindi-shared";
import { introduce, review } from "@repo/hindi-shared";

const STORAGE_KEY = "hindi.state.v1";

/** Client-persisted learner state — the single source of truth in the browser. */
export interface AppState {
  reviews: Record<string, ReviewState>;
  completedLessonIds: string[];
  sessionCount: number;
  settings: {
    length: 30 | 45;
    paperEvery: number;
    voiceName: string | null;
    rate: number;
  };
  /** Per-review-item last-write-wins stamps (epoch ms), used by sync in Task 12. */
  updatedAt: Record<string, number>;
}

function defaultState(): AppState {
  return {
    reviews: {},
    completedLessonIds: [],
    sessionCount: 0,
    settings: { length: 30, paperEvery: 4, voiceName: null, rate: 0.85 },
    updatedAt: {},
  };
}

/** Narrow, defensive shape check — anything short of this shape is discarded. */
function isAppState(value: unknown): value is AppState {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate["reviews"] !== "object" ||
    candidate["reviews"] === null
  ) {
    return false;
  }
  if (!Array.isArray(candidate["completedLessonIds"])) return false;
  if (typeof candidate["sessionCount"] !== "number") return false;
  if (
    typeof candidate["settings"] !== "object" ||
    candidate["settings"] === null
  ) {
    return false;
  }
  if (
    typeof candidate["updatedAt"] !== "object" ||
    candidate["updatedAt"] === null
  ) {
    return false;
  }
  return true;
}

/** Load persisted state from `localStorage`, tolerating corrupt/missing data. */
export function loadState(): AppState {
  if (typeof window === "undefined") return defaultState();

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return defaultState();

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isAppState(parsed)) return defaultState();
    return parsed;
  } catch {
    return defaultState();
  }
}

/** Persist state to `localStorage`. */
export function saveState(s: AppState): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

/**
 * Grade a review item: introduces it first if it has no prior review state,
 * then scores the review. Stamps `updatedAt[itemId]` to `now`.
 *
 * `kind` is accepted for call-site clarity and future validation hooks (it
 * is not stored — `ReviewState` carries no `kind` field; content registries
 * derive kind from `itemId` at sync time) but is otherwise unused here.
 */
export function applyGrade(
  s: AppState,
  itemId: string,
  _kind: ItemKind | "word" | "sentence",
  grade: Grade,
  now: number,
  rand: () => number,
): AppState {
  const existing = s.reviews[itemId] ?? introduce(itemId, now);
  const updated = review(existing, grade, now, rand);

  return {
    ...s,
    reviews: { ...s.reviews, [itemId]: updated },
    updatedAt: { ...s.updatedAt, [itemId]: now },
  };
}

/**
 * Mark a lesson complete (idempotent — appends only if not already present).
 *
 * `now` is accepted for signature symmetry with {@link applyGrade} and to
 * leave room for a future per-lesson completion timestamp; `completedLessonIds`
 * itself carries no ordering/time semantics per the brief, so nothing is
 * stamped into `updatedAt` (that map is per-review-item only).
 */
export function completeLesson(
  s: AppState,
  lessonId: string,
  _now: number,
): AppState {
  if (s.completedLessonIds.includes(lessonId)) {
    return s;
  }
  return {
    ...s,
    completedLessonIds: [...s.completedLessonIds, lessonId],
  };
}
